import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { NotificationService } from 'app/shared/notifications/notification.service';
import { RestService } from '../../../rest.service';
import { AccordionConfig, AccordionItem } from '../../../modals-results-categories/accordion-info-product/accordion-info.interface';
import { ICancellationPolicyModalResultsDisplayData } from 'model/view-models/results/product-card/price-display-data.interface';
import { IParkStep } from '../park-steps/park-steps.component';
import { parkTicketInfo } from '../park-tickets/park-tickets.component';
import { IParkRoomChoice } from '../park-rooms/park-rooms.component';
import { IParkAccessChoice } from '../park-access/park-access.component';
import { IParkMealChoice } from '../park-meals/park-meals.component';
import {
  ACCESSIBILITY_LABELS,
  FULL_OF_DREAMS_ID,
  ParkCatalogIndex,
  PLAN_NAMES,
  PLAN_OF_LEVEL,
  PLAN_ORDER,
  offerOfProvider,
  packagesOf,
  ticketPriceOn,
  variantPackages,
} from './park-catalog';

export type ParkStepKey = 'hotel' | 'meals' | 'tickets' | 'days' | 'summary';

const STEP_LABELS: { [key in ParkStepKey]: { pre: string; strong: string } } = {
  hotel: { pre: 'Selección de', strong: 'Hotel' },
  meals: { pre: 'Selección de', strong: 'Plan de Comidas' },
  tickets: { pre: 'Selección de', strong: 'Tickets' },
  days: { pre: 'Selección de', strong: 'Días' },
  summary: { pre: 'Resumen', strong: 'Carrito' },
};

/** Línea del detalle de tarifas (pie de página y resumen). */
export interface IParkFareRow {
  label: string;
  detail: string;
  amount: number;
}

/** Lo que el pie de página del modal necesita para dibujarse. */
export interface IParkFlowState {
  steps: IParkStep[];
  current: number;
  maxReached: number;
  hasChoice: boolean;
  canNext: boolean;
  canBack: boolean;
  nextLabel: string;
  loading: boolean;
  lines: string[];
  rows: IParkFareRow[];
  summary: boolean;          // en el resumen los botones van dentro del contenido, no en el pie
  accommodationTotal: number;
  netTotal: number;
  currency: string;
}

/** Selección final, lista para agregar al carrito. */
export interface IParkFlowResult {
  real: any;                 // habitación real (additionalInfo.rooms[]) de la oferta elegida
  offer: any;
  roomType: any;
  packageCode: string;       // HN, TN, TQ...
  plan: string;
  ticket: any | null;        // opción de tickets.options[] (null si es solo hotel)
  days: string | null;
  ticketDate: string | null; // fecha de inicio del ticket
  ticketKey: string | null;  // ticketKey del ticket: entra como rateKey/bookingCode de la reserva (solo ticket en paquete)
  addon: boolean;            // el ticket es un add-on (Universal): producto aparte, la tarifa del hotel no cambia
  accommodationTotal: number;
  netTotal: number;
  currency: string;
  accessKind: string | null;
}

/**
 * Configurador de compra de Parques. El paso a paso va construyendo la oferta final del roomCatalog:
 *   1. Hotel: tipo de habitación + proveedor y, en la misma pantalla, la accesibilidad.
 *   2. Plan de comidas.
 *   3. Tickets: solo si el proveedor los vende para el plan elegido; los tickets se cargan (checkAvail con
 *      withTickets) al entrar a este paso, con la tarifa ya definida. "Solo hotel" no los necesita.
 *   4. Días: días de parque del ticket elegido y fecha en el calendario.
 *   5. Resumen.
 * Hay dos modelos de ticket:
 *   - Paquete (Disney, Action Travel): el catálogo trae paquetes T* y el ticketKey del ticket reemplaza al de la
 *     habitación (se puede iniciar el ticket cualquier día de la estadía; el precio solo cambia con los días).
 *   - Add-on (Universal, Full of Dreams): el catálogo solo trae paquetes H*; el ticket se suma al precio de la
 *     habitación, se reserva aparte y cada fecha disponible trae su precio (tickets.mode === 'addon').
 * Cada paso se limita al proveedor elegido. Una variante accesible sigue el mismo camino que la estándar, con los
 * paquetes que realmente tenga (el catálogo solo trae su tarifa más barata: ver variantPackages); si solo tiene
 * habitación, va directo al resumen.
 * El ticketKey del ticket está atado a la tarifa (plan), por eso el plan se elige antes que los tickets.
 */
@Component({
  selector: 'usb-park-flow',
  templateUrl: './park-flow.component.html',
  styleUrls: ['./park-flow.component.scss'],
})
export class ParkFlowComponent implements OnChanges {
  @Input() hotel: any;
  @Input() arrivalDate: string | Date;
  @Input() departureDate: string | Date;
  @Input() adults = 1;
  @Input() childAges: any[] = [];
  @Input() address = '';
  @Input() currencies: any[];
  @Input() currencyAgency: any;
  @Input() buttonColorActive: string;
  @Input() buttonColorActiveDark: string;
  @Input() clientSetting: any;

  @Output() stateChange = new EventEmitter<IParkFlowState>();
  @Output() complete = new EventEmitter<IParkFlowResult>();
  @Output() photos = new EventEmitter<void>();

  index: ParkCatalogIndex = new ParkCatalogIndex([]);

  stepKey: ParkStepKey = 'hotel';
  private visited = new Set<ParkStepKey>(['hotel']);

  choice: IParkRoomChoice | null = null;
  accessKey = 'standard';
  ticketMode: string | null = null;     // null = sin elegir, 'none' = solo hotel, o tipo de ticket
  days: string | null = null;
  ticketKey: string | null = null;      // ticket elegido (options[].key, no el options[].ticketKey): varios pueden compartir tipo y días
  ticketDate: string | null = null;     // fecha de inicio del ticket
  plan = 'N';

  private accessCacheKey = '';
  private accessCache: { [code: string]: any[] } = {};

  tickets: any = null;
  textExpanded = false;
  ticketsFor = '';                      // rateKey de la tarifa con la que se pidieron los tickets
  ticketConfirmedFor = '';              // ticketKey del ticket ya confirmado con checkAvail al llegar al resumen
  // Full of Dreams (oferta con requiresCheckAvail): el catálogo solo trae la habitación. Los planes de comidas llegan en
  // un checkAvail (sin tickets) que se pide al salir del paso de hotel; los tickets se piden después con la clave del plan
  // elegido y el resumen se confirma con el ticketKey (o la clave del plan si no hay ticket).
  private checkAvailData: { baseKey: string; packages: { [code: string]: any[] } } | null = null;
  private confirmedRateKey = '';        // price.rateKey del último checkAvail de confirmación: es con el que se reserva
  private confirmedPolicy: any[] | null = null;  // política de cancelación que devolvió esa confirmación
  loading = false;
  error = '';

  constructor(
    private restService: RestService,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['hotel']) {
      this.index = new ParkCatalogIndex(this.hotel?.additionalInfo?.rooms || []);
      this.checkAvailData = null;
      this.hotelType = this.resolveHotelType();
      this.accessCacheKey = '';
    }
    this.commit();
  }

  // ------------------------------------------------------------------ catálogo
  get roomTypes(): any[] {
    return this.hotel?.additionalInfo?.roomCatalog?.roomTypes || [];
  }

  get nights(): number {
    return this.hotel?.additionalInfo?.roomCatalog?.nights || 0;
  }

  get roomType(): any {
    return this.choice?.roomType || null;
  }

  get providerId(): string {
    return this.choice?.providerId || '';
  }

  get provider(): { id: string; name: string; logo: string } {
    const offer = this.finalOffer || this.standardOffer;
    return offer ? this.index.provider(offer) : { id: this.providerId, name: '', logo: '' };
  }

  // Paquetes de lo elegido: los del checkAvail (si la oferta lo exige) o los del catálogo.
  private get activePackages(): { [code: string]: any[] } {
    return this.checkAvail?.packages || this.rawPackages;
  }

  // Paquetes del catálogo: los del tipo de habitación o, en una variante accesible, los de esa variante.
  private get rawPackages(): { [code: string]: any[] } {
    if (!this.accessVariant) {
      return packagesOf(this.roomType);
    }
    const key = `${this.roomType?.typeKey}|${this.providerId}|${this.accessKey}`;
    if (this.accessCacheKey !== key) {
      this.accessCacheKey = key;
      this.accessCache = variantPackages(this.accessOffer, this.providerId, this.hotel?.additionalInfo?.rooms || []);
    }
    return this.accessCache;
  }

  // La variante accesible elegida tiene paquetes propios; si no, se reserva solo la habitación con su tarifa.
  get hasVariantPackages(): boolean {
    return !this.accessVariant || Object.keys(this.activePackages).length > 0;
  }

  // Oferta de lo elegido que exige un checkAvail para conocer sus planes de comidas y tickets (Full of Dreams).
  private get checkAvailOffer(): any {
    if (!this.choice) {
      return null;
    }
    const raw = this.rawPackages;
    const offers = Object.keys(raw).map(code => offerOfProvider(raw[code], this.providerId)).filter(o => !!o);
    if (!offers.length && this.accessVariant) {
      offers.push(this.accessOffer);
    }
    const found = offers.find(o => o?.requiresCheckAvail === true);
    return found && this.index.real(found) ? found : null;
  }

  // Datos del checkAvail ya pedido para lo elegido (null si no aplica o todavía no se pidió).
  private get checkAvail(): { baseKey: string; packages: { [code: string]: any[] } } | null {
    const data = this.checkAvailData;
    return data && data.baseKey === this.checkAvailOffer?.rateKey ? data : null;
  }

  // Los planes y tickets de lo elegido salen de un checkAvail (el paso de comidas no filtra por paquetes T*).
  get usesCheckAvail(): boolean {
    return !!this.checkAvail;
  }

  // Tipo de habitación con los paquetes de lo elegido (el paso de comidas lee roomType.packages).
  get mealsRoomType(): any {
    if (!this.checkAvail && (!this.accessVariant || !this.hasVariantPackages)) {
      return this.roomType;
    }
    const key = `${this.roomType?.typeKey}|${this.providerId}|${this.accessKey}|${this.checkAvail?.baseKey || ''}`;
    if (this.mealsRoomTypeKey !== key) {
      this.mealsRoomTypeKey = key;
      this.mealsRoomTypeCache = { ...this.roomType, packages: this.activePackages };
    }
    return this.mealsRoomTypeCache;
  }

  private mealsRoomTypeKey = '';
  private mealsRoomTypeCache: any = null;

  private planOffer(prefix: string, plan: string): any {
    const offer = offerOfProvider(this.activePackages[prefix + plan], this.providerId);
    return offer && this.index.real(offer) ? offer : null;
  }

  private plansFor(prefix: string): string[] {
    return PLAN_ORDER.filter(p => !!this.planOffer(prefix, p));
  }

  // Oferta estándar más barata del proveedor (para el "desde" de accesibilidad).
  get standardOffer(): any {
    if (!this.roomType) {
      return null;
    }
    const offers = Object.keys(packagesOf(this.roomType))
      .map(code => this.planOfferByCode(code))
      .filter(o => !!o);
    return this.index.cheapest(offers);
  }

  private planOfferByCode(code: string): any {
    const offer = offerOfProvider(packagesOf(this.roomType)[code], this.providerId);
    return offer && this.index.real(offer) ? offer : null;
  }

  get accessVariants(): any[] {
    return (this.roomType?.accessibility || []).filter((v: any) => {
      const offer = offerOfProvider(v.offers, this.providerId);
      return !!offer && !!this.index.real(offer);
    });
  }

  get accessVariant(): any {
    return this.accessKey === 'standard' ? null : (this.roomType?.accessibility || [])[Number(this.accessKey)] || null;
  }

  get accessOffer(): any {
    const v = this.accessVariant;
    return v ? offerOfProvider(v.offers, this.providerId) : null;
  }

  // Universal: el ticket no es un paquete del catálogo sino un add-on del hotel que vende Full of Dreams.
  get addonTickets(): boolean {
    return this.hotelType === 'UNIVERSAL'
      && this.providerId === FULL_OF_DREAMS_ID
      && !this.planOffer('T', this.plan)
      && !!this.planOffer('H', this.plan);
  }

  // Hay tickets para el plan elegido: paquete T* del proveedor (Disney) o add-on (Universal).
  get ticketsPossible(): boolean {
    return !!this.planOffer('T', this.plan) || this.addonTickets || (!!this.checkAvail && this.planOffer('H', this.plan)?.allowsTickets !== false);
  }

  // "Solo hotel" solo se ofrece si el proveedor tiene paquetes sin tickets.
  get allowNone(): boolean {
    return !!this.planOffer('H', this.plan);
  }

  get withTickets(): boolean {
    return !!this.ticketMode && this.ticketMode !== 'none';
  }

  // Paquete de solo hotel; si el proveedor solo vende paquetes con tickets, se usa el T*.
  // Se mira el plan elegido: un plan puede venderse solo con tickets (T*) aunque el proveedor tenga habitación sola (HN).
  private get hotelPrefix(): string {
    return this.planOffer('H', this.plan) ? 'H' : 'T';
  }

  // Planes de comidas del proveedor, con o sin tickets (Action Travel vende los planes solo en paquetes T*).
  get availablePlans(): string[] {
    return PLAN_ORDER.filter(p => !!this.planOffer('H', p) || !!this.planOffer('T', p));
  }

  private get prefix(): string {
    return this.withTickets && !this.addonTickets && !this.checkAvail ? 'T' : this.hotelPrefix;
  }

  // Tarifa con la que se piden los tickets: el paquete T* del plan elegido o, en un add-on, la del hotel.
  private get ticketBaseOffer(): any {
    return this.planOffer('T', this.plan) || (this.addonTickets || this.checkAvail ? this.planOffer('H', this.plan) : null);
  }

  get finalOffer(): any {
    if (!this.choice) {
      return null;
    }
    return this.accessVariant && !this.hasVariantPackages ? this.accessOffer : this.planOffer(this.prefix, this.plan);
  }

  get packageCode(): string {
    if (this.checkAvail && this.withTickets && !this.addonTickets) {
      return 'T' + this.plan;
    }
    return this.accessVariant && !this.hasVariantPackages ? 'HN' : this.prefix + this.plan;
  }

  // Rango en que se puede iniciar el ticket: el de la búsqueda (llegada → salida), en formato YYYY-MM-DD.
  private get stayFrom(): string {
    return this.isoDate(this.arrivalDate);
  }

  private get stayTo(): string {
    return this.isoDate(this.departureDate) || this.stayFrom;
  }

  private isoDate(value: string | Date): string {
    if (!value) {
      return '';
    }
    if (value instanceof Date) {
      return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
    }
    return String(value).substring(0, 10);
  }

  get ticket(): any {
    if (!this.withTickets || !this.tickets) {
      return null;
    }
    const sameDays = (this.tickets.options || []).filter((o: any) => o.type === this.ticketMode && String(o.days) === this.days);
    return sameDays.find((o: any) => o.key === this.ticketKey) || sameDays[0] || null;
  }

  private get ticketDelta(): number {
    const t = this.ticket;
    if (!t || (this.tickets?.currency || 'USD') !== this.currency) {
      return 0;
    }
    if (this.addonTickets) {
      // El add-on se suma completo y su precio depende de la fecha elegida.
      return ticketPriceOn(t, this.ticketDate, this.adults, this.childAges?.length || 0);
    }
    if (t.priceDeltaProf == null && this.checkAvail) {
      // Full of Dreams no manda priceDeltaProf: el delta viene neto y se le aplica la misma rentabilidad de la tarifa.
      return Number(t.priceDelta ?? 0) * this.profitRatio;
    }
    return Number(t.priceDeltaProf ?? t.priceDelta ?? 0);
  }

  // Rentabilidad de la tarifa elegida (precio con rentabilidad / precio neto del proveedor); 1 si no se conoce.
  private get profitRatio(): number {
    const room = this.index.real(this.finalOffer)?.room;
    const sale = Number(room?.optionNightsNetTotalProf);
    const net = Number(room?.pricing?.priceWithoutTax);
    return sale > 0 && net > 0 ? sale / net : 1;
  }

  get currency(): string {
    return this.finalOffer ? this.index.currency(this.finalOffer) : 'USD';
  }

  get accommodationTotal(): number {
    return this.finalOffer ? this.index.price(this.finalOffer) : 0;
  }

  get netTotal(): number {
    return this.accommodationTotal + this.ticketDelta;
  }

  get ticketTitle(): string {
    return parkTicketInfo(this.hotelType, this.ticketMode || '')?.title || '';
  }

  get planName(): string {
    return PLAN_NAMES[this.plan] || '';
  }

  get accessLabel(): string {
    const v = this.accessVariant;
    return v ? ACCESSIBILITY_LABELS[v.kind] || v.kind || 'Habitación accesible' : '';
  }

  get cancellationText(): string {
    const offer = this.finalOffer;
    if (!offer) {
      return '';
    }
    if (!this.index.isRefundable(offer)) {
      return 'No reembolsable';
    }
    const from = offer.cancelFrom;
    return from ? 'Cancelación gratuita hasta ' + String(from).substring(0, 10).split('-').reverse().join('/') : 'Reembolsable';
  }

  // Políticas de cancelación de la tarifa elegida (la que devolvió la confirmación o, si no, la del catálogo).
  private get policies(): any[] {
    // Un array vacío cuenta como "sin dato": se pasa a la siguiente fuente.
    return this.confirmedPolicy || this.policyOf(this.index.real(this.finalOffer)) || [];
  }

  // La política viene dentro de room (catálogo de ActionTravel) o al lado de room (checkAvail de Full of Dreams).
  private policyOf(wrapper: any): any[] | null {
    return [wrapper?.room?.cancellationPolicy, wrapper?.cancellationPolicy].find((p: any) => p?.length) || null;
  }

  get hasPolicies(): boolean {
    return this.policies.length > 0;
  }

  // Textos que manda el proveedor (Full of Dreams); el detalle por fechas lo dibuja usb-cancellation-policies.
  get policyLines(): string[] {
    return this.policies.map((p: any) => String(p?.description || '')).filter(Boolean);
  }

  // Acordeón de políticas: mismo componente y plantilla que usan las demás categorías.
  readonly policyItems: AccordionItem[] = [{ id: 'policies', title: 'Políticas de Cancelación y Penalidades' }];
  readonly policyConfig: AccordionConfig = { multiple: false, animated: true, showArrows: true, borderTop: false, defaultOpen: [] };

  private policyDataKey = '';
  private policyData: ICancellationPolicyModalResultsDisplayData | null = null;

  get cancellationData(): ICancellationPolicyModalResultsDisplayData {
    const ratio = this.checkAvail ? this.profitRatio : 1;
    // Full of Dreams no manda amountProf: se le aplica la rentabilidad de la tarifa.
    const list = this.policies.map((p: any) => (p?.amountProf == null && ratio !== 1 ? { ...p, amountProf: Math.round(Number(p.amount) * ratio * 100) / 100 } : p));
    const key = JSON.stringify([list, this.arrivalDate, this.refundable]);
    if (key !== this.policyDataKey || !this.policyData) {
      const charged = list.find((c: any) => Number(c.amount) > 0);
      this.policyDataKey = key;
      this.policyData = {
        rateClass: this.refundable ? 'R' : 'N',
        cancellationPolicies: list,
        currencyCancellation: list[0]?.currency || this.currency,
        amount: charged?.amountProf || 0,
        from: charged?.from || 'N/A',
        cancellationFree: list.some((c: any) => Number(c.amount) === 0),
        arrivalDate: this.isoDate(this.arrivalDate),
      };
    }
    return this.policyData;
  }

  get refundable(): boolean {
    return !!this.finalOffer && this.index.isRefundable(this.finalOffer);
  }

  // Días de la estadía (noches + 1), como en el resumen del diseño.
  get stayDays(): number {
    const from = this.stayFrom;
    const to = this.stayTo;
    if (!from || !to) {
      return 0;
    }
    const days = Math.round((Date.parse(to) - Date.parse(from)) / 86400000) + 1;
    return days > 0 ? days : 0;
  }

  get paxText(): string {
    const children = this.childAges?.length || 0;
    const adults = `${this.adults} Adulto${this.adults === 1 ? '' : 's'}`;
    return children ? `${adults} - ${children} Niño${children === 1 ? '' : 's'}` : adults;
  }

  get ticketPaxText(): string {
    const children = this.childAges?.length || 0;
    const adults = `${this.adults}X Adulto`;
    return children ? `${adults} - ${children}X Niño` : adults;
  }

  get durationText(): string {
    const t = this.ticket;
    if (!t) {
      return 'Elige los días de parque del ticket.';
    }
    return `${t.days} ${Number(t.days) === 1 ? 'día' : 'días'} de parque desde la fecha de inicio que elijas.`;
  }

  // Texto fijo del paso de días: qué permite cada tipo de entrada según el parque de la búsqueda.
  get ticketsText(): string {
    return this.hotelType === 'UNIVERSAL'
      ? 'Elige la opción de entrada a Universal Orlando que mejor se adapte a tu forma de disfrutar las vacaciones. La entrada para 1 Parque por Día permite visitar un parque temático diferente cada día de tu reserva. La entrada Park-to-Park ofrece la flexibilidad de recorrer varios parques temáticos el mismo día, además de disfrutar experiencias como el Hogwarts Express entre parques.'
      : 'Elige la opción de entrada a Walt Disney World que mejor se adapte a tu forma de disfrutar las vacaciones. La entrada de Un Parque por Día permite visitar un parque temático diferente cada día de tu ticket. Con Park Hopper puedes recorrer varios parques temáticos el mismo día, y las opciones Plus suman visitas a los parques acuáticos y otras experiencias de Walt Disney World.';
  }

  // Galería del paso de días: fotos fijas de Disney o, si no, las del hotel.
  get gallery(): string[] {
    if (this.hotelType !== 'UNIVERSAL') {
      return [1, 2, 3].map(n => `/assets/img/parks/disney/gallery/${n}.jpg`);
    }
    return this.images.slice(0, 3);
  }

  // Desglose del total: alojamiento (solo habitación), plan de comidas (diferencia con el mismo paquete sin plan)
  // y ticket (el resto). Sale del roomCatalog: el plan y el ticket vienen dentro de la tarifa, no como precio aparte.
  get breakdown(): { lodging: number; meals: number; ticket: number } {
    if (!this.finalOffer) {
      return { lodging: 0, meals: 0, ticket: 0 };
    }
    if (this.accessVariant && !this.hasVariantPackages) {
      return { lodging: this.accommodationTotal, meals: 0, ticket: 0 };
    }
    const priceOf = (prefix: string, plan: string): number | null => {
      const offer = this.planOffer(prefix, plan);
      return offer ? this.index.price(offer) : null;
    };
    // El plan se mide contra el mismo paquete sin plan (HN o TN); el alojamiento es la habitación sola si existe.
    const pre = this.prefix;
    const sameBase = priceOf(pre, 'N');
    const withPlan = priceOf(pre, this.plan) ?? this.accommodationTotal;
    const meals = sameBase !== null ? Math.max(withPlan - sameBase, 0) : 0;
    const lodging = priceOf('H', 'N') ?? sameBase ?? withPlan;
    const ticket = this.withTickets ? Math.max(this.netTotal - lodging - meals, 0) : 0;
    return { lodging, meals, ticket };
  }

  // Tipo de parque de la búsqueda (DISNEY, UNIVERSAL): define la información fija y el modelo de tickets.
  hotelType = '';

  private resolveHotelType(): string {
    const search = (JSON.parse(localStorage.getItem('payload') || '[]') || [])[0] || {};
    return String(this.hotel?.hotelType || search.others?.hotelType || '').toUpperCase();
  }

  get images(): string[] {
    return (this.hotel?.additionalInfo?.images || []).map((i: any) => i?.image || i).filter(Boolean);
  }

  get fallbackImage(): string {
    return this.images[0] || '/assets/img/app/hotel_default.png';
  }

  // ------------------------------------------------------------------ pasos
  get stepList(): ParkStepKey[] {
    const list: ParkStepKey[] = ['hotel'];
    if (!this.choice) {
      list.push('meals', 'tickets', 'days');
    } else if (this.hasVariantPackages) {
      if (this.availablePlans.length > 1) {
        list.push('meals');
      }
      if (this.ticketsPossible) {
        list.push('tickets');
        if (this.ticketMode !== 'none') {
          list.push('days');
        }
      }
    }
    list.push('summary');
    return list;
  }

  get steps(): IParkStep[] {
    return this.stepList.map((key, i) => ({ id: i + 1, ...STEP_LABELS[key] }));
  }

  private get stepIndex(): number {
    return Math.max(this.stepList.indexOf(this.stepKey), 0);
  }

  private get maxReached(): number {
    const list = this.stepList;
    return list.reduce((max, key, i) => (this.visited.has(key) ? Math.max(max, i) : max), 0);
  }

  get canNext(): boolean {
    if (this.loading) {
      return false;
    }
    switch (this.stepKey) {
      case 'hotel': return !!this.choice;
      case 'meals': return this.availablePlans.includes(this.plan);
      case 'tickets':
        return this.ticketMode === 'none'
          || (this.withTickets && !!this.ticket && this.hasTicketsFor(this.ticketBaseOffer));
      case 'days': return !!this.ticket && !!this.ticketDate;
      case 'summary':
        return !!this.finalOffer
          && (!this.withTickets || (!!this.ticket && !!this.ticketDate && this.hasTicketsFor(this.finalOffer)))
          && (!this.confirmKey || this.ticketConfirmedFor === this.confirmKey);
    }
    return false;
  }

  next(): void {
    if (!this.canNext) {
      return;
    }
    if (this.stepKey === 'summary') {
      this.complete.emit(this.result());
      return;
    }
    const offer = this.checkAvailOffer;
    if (this.stepKey === 'hotel' && offer && !this.checkAvail) {
      this.loadCheckAvail(offer, () => this.enter(this.stepList[this.stepIndex + 1]));
      return;
    }
    this.enter(this.stepList[this.stepIndex + 1]);
  }

  back(): void {
    if (this.stepIndex > 0) {
      this.enter(this.stepList[this.stepIndex - 1]);
    }
  }

  goTo(id: number): void {
    const key = this.stepList[id - 1];
    if (key && id - 1 <= this.maxReached) {
      this.enter(key);
    }
  }

  private enter(key: ParkStepKey): void {
    this.stepKey = key;
    this.visited.add(key);
    if (key === 'meals') {
      this.normalizePlan();
    }
    if (key === 'tickets') {
      this.ensureTickets();
    }
    // La confirmación del ticket vale solo para esta llegada al resumen: el ticketKey vence. El error de otro paso no se arrastra.
    this.ticketConfirmedFor = '';
    this.confirmedRateKey = '';
    this.confirmedPolicy = null;
    if (key !== 'tickets') {
      this.error = '';
    }
    if (key === 'summary') {
      this.confirmTicket();
    }
    this.commit();
  }

  // ------------------------------------------------------------------ elecciones
  onRoomChoose(c: IParkRoomChoice): void {
    const same = this.choice?.roomType?.typeKey === c.roomType?.typeKey && this.choice?.providerId === c.providerId;
    if (!same) {
      this.choice = c;
      this.accessKey = 'standard';
      this.ticketMode = null;
      this.days = null;
      this.ticketKey = null;
      this.ticketDate = null;
      this.plan = 'N';
      this.tickets = null;
      this.ticketsFor = '';
      this.error = '';
      this.visited = new Set<ParkStepKey>(['hotel']);
      this.normalizePlan();
    }
    this.commit();
  }

  onAccessChoose(c: IParkAccessChoice): void {
    if (this.accessKey !== c.key) {
      // Cada variante tiene sus propios paquetes: se empieza de nuevo la configuración desde el plan.
      this.accessKey = c.key;
      this.ticketMode = null;
      this.days = null;
      this.ticketKey = null;
      this.ticketDate = null;
      this.plan = 'N';
      this.tickets = null;
      this.ticketsFor = '';
      this.error = '';
      this.visited = new Set<ParkStepKey>(['hotel']);
      this.normalizePlan();
    }
    this.commit();
  }

  onTicketChoose(mode: string): void {
    this.ticketMode = mode;
    if (mode === 'none') {
      this.days = null;
      this.ticketKey = null;
      this.ticketDate = null;
    } else {
      this.pickTicket(mode);
    }
    this.commit();
  }

  onDaysChoose(option: { value: string; key: string }): void {
    this.days = option.value;
    this.ticketKey = option.key;
    this.syncTicketDate();
    this.commit();
  }

  // Ticket del tipo elegido: el mismo (key), si no uno con los mismos días, si no el preseleccionado o el primero.
  private pickTicket(type: string): void {
    const options: any[] = (this.tickets?.options || []).filter((o: any) => o.type === type);
    const chosen = options.find(o => o.key === this.ticketKey)
      || options.find(o => String(o.days) === this.days)
      || options.find(o => o.selected)
      || options[0];
    this.days = chosen ? String(chosen.days) : null;
    this.ticketKey = chosen?.key || null;
    this.syncTicketDate();
  }

  onDateChoose(date: string): void {
    this.ticketDate = date;
    this.commit();
  }

  // La fecha del ticket debe caer dentro del rango buscado; si no, se usa la de llegada.
  // En un add-on solo valen las fechas con disponibilidad del ticket elegido (ticket.dates).
  private syncTicketDate(): void {
    if (this.addonTickets) {
      const dates: string[] = ((this.ticket?.dates || []) as any[]).map(d => String(d?.date || '').substring(0, 10)).filter(Boolean);
      if (!this.ticketDate || !dates.includes(this.ticketDate)) {
        this.ticketDate = dates[0] || null;
      }
      return;
    }
    const inRange = !!this.ticketDate && this.ticketDate >= this.stayFrom && this.ticketDate <= this.stayTo;
    if (!inRange) {
      this.ticketDate = this.stayFrom || null;
    }
  }

  onMealChoose(c: IParkMealChoice): void {
    this.plan = c.plan;
    // Los tickets están atados a la tarifa: con otro plan hay que pedirlos de nuevo (se conserva tipo y días).
    if (!this.hasTicketsFor(this.ticketBaseOffer)) {
      this.tickets = null;
      this.ticketsFor = '';
    }
    this.ensureTicketsForPlan();
    this.commit();
  }

  // Si el plan elegido no existe en el paquete actual, se usa el primero disponible.
  private normalizePlan(): void {
    const plans = this.withTickets ? this.plansFor('T') : this.availablePlans;
    if (plans.length && !plans.includes(this.plan)) {
      this.plan = plans[0];
    }
  }

  // ------------------------------------------------------------------ tickets (checkAvail withTickets)
  // Al entrar al paso de tickets: se cargan solo si hace falta (otra tarifa o todavía no cargados).
  private ensureTickets(): void {
    const base = this.ticketBaseOffer;
    if (base && !this.hasTicketsFor(base)) {
      this.fetchTickets(base);
    }
  }

  // Los tickets cargados son los de esta tarifa (el ticketKey depende del plan de comidas).
  private hasTicketsFor(offer: any): boolean {
    return !!offer && !!this.tickets && this.ticketsFor === offer.rateKey;
  }

  // Con tickets ya elegidos, un cambio de plan recarga los tickets de la nueva tarifa.
  private ensureTicketsForPlan(): void {
    if (this.withTickets && this.finalOffer && !this.hasTicketsFor(this.finalOffer)) {
      this.fetchTickets(this.finalOffer);
    }
  }

  // Si el proveedor no tiene tickets para esta habitación, se sigue solo con el hotel.
  skipTickets(): void {
    this.ticketMode = 'none';
    this.days = null;
    this.ticketDate = null;
    this.next();
  }

  retryTickets(): void {
    this.ticketsFor = '';
    if (this.stepKey === 'tickets') {
      this.ensureTickets();
    } else {
      this.ensureTicketsForPlan();
    }
  }

  // Solo opciones normalizadas por Octopus (con tipo, días y ticketKey); otras no se pueden comprar desde aquí.
  // Octopus llamaba bookRef a lo que hoy es ticketKey: se acepta cualquiera de los dos.
  private normalizeTickets(tickets: any): any {
    const options = (tickets?.options || [])
      .map((o: any) => ({ ...o, ticketKey: o?.ticketKey || o?.bookRef }))
      .filter((o: any) => !!o.ticketKey && !!o.type && Number(o.days) > 0);
    return options.length ? { ...tickets, options } : null;
  }

  // checkAvail de la oferta con requiresCheckAvail (sin tickets): trae los planes de comidas (diningPlans) de esa
  // habitación. Cada plan pasa a ser una oferta H* con su rateKey y su habitación real; los tickets se piden luego.
  private loadCheckAvail(offer: any, done: () => void): void {
    const real = this.index.real(offer);
    this.loading = true;
    this.error = '';
    this.commit();
    this.restService.doPost('app', 'search/products/multiple', this.availPayload(offer, real, false)).subscribe(
      (res: any) => {
        this.loading = false;
        const info = this.extractResult(res)?.additionalInfo;
        const checked = info?.rooms?.[0];
        if (!checked) {
          this.fail(this.extractError(res) || 'No se pudo consultar la disponibilidad de esta habitación.');
          return;
        }
        // El check puede devolver roomType "Room": se conserva el nombre del search.
        const baseReal = { ...real, ...checked, room: { ...real.room, ...checked.room, roomType: real.room.roomType, name: real.room.name } };
        const plans = (info.diningPlans || []).filter((p: any) => !!p?.key && !!PLAN_OF_LEVEL[p.level]);
        const packages: { [code: string]: any[] } = {};
        (plans.length ? plans : [{ key: offer.rateKey, level: 'none', name: offer.name, totalPrice: offer.price, priceDelta: 0 }]).forEach((p: any) => {
          const price = Number(p.totalPrice ?? offer.price);
          // Los montos del search son los de la habitación sola (neto y con rentabilidad): cada plan los escala por la
          // misma proporción que su precio neto, así el neto y la rentabilidad de la tarifa siguen siendo coherentes.
          const baseNet = Number(real.room.pricing?.priceWithoutTax ?? baseReal.room.pricing?.priceWithoutTax);
          const f = baseNet > 0 ? price / baseNet : 1;
          const sc = (v: any) => (typeof v === 'number' ? Math.round(v * f * 100) / 100 : v);
          const r = baseReal.room;
          this.index.set(p.key, {
            ...baseReal,
            room: {
              ...r,
              rateKey: p.key,
              bookParam: p.key,
              boardName: p.name || r.boardName,
              netPriceUnitary: sc(r.netPriceUnitary),
              netPriceFreeTax: sc(r.netPriceFreeTax),
              netPriceProf: sc(r.netPriceProf),
              optionNightsNetTotalProf: sc(r.optionNightsNetTotalProf),
              optionNightsNetTotalFreeTax: sc(r.optionNightsNetTotalFreeTax),
              profility: r.profility
                ? {
                    ...r.profility,
                    netPrice: sc(r.profility.netPrice),
                    netPriceCategory: sc(r.profility.netPriceCategory),
                    netPriceProvider: sc(r.profility.netPriceProvider),
                    netPriceBooking: sc(r.profility.netPriceBooking),
                    blickoins: sc(r.profility.blickoins),
                    creditBlickoins: sc(r.profility.creditBlickoins),
                  }
                : r.profility,
              pricing: { ...r.pricing, priceWithTax: price, priceWithoutTax: price },
            },
          });
          packages['H' + PLAN_OF_LEVEL[p.level]] = [{ ...offer, rateKey: p.key, price, name: p.name || offer.name, requiresCheckAvail: false, allowsTickets: p.allowsTickets }];
        });
        this.checkAvailData = { baseKey: offer.rateKey, packages };
        this.normalizePlan();
        done();
      },
      () => {
        this.loading = false;
        this.fail('No se pudo consultar la disponibilidad de esta habitación. Inténtalo de nuevo.');
      },
    );
  }

  private fetchTickets(offer: any): void {
    const real = this.index.real(offer);
    if (!real) {
      return;
    }
    const payload = this.availPayload(offer, real, true);

    this.loading = true;
    this.error = '';
    this.commit();
    this.restService.doPost('app', 'search/products/multiple', payload).subscribe(
      (res: any) => {
        this.loading = false;
        const tickets = this.normalizeTickets(this.extractTickets(res));
        if (!tickets) {
          this.fail(this.extractError(res) || 'No se encontraron tickets disponibles para esta habitación.');
          return;
        }
        this.tickets = tickets;
        this.ticketsFor = offer.rateKey;
        this.remapTicketSelection();
        this.commit();
      },
      () => {
        this.loading = false;
        this.fail('No se pudieron obtener los tickets. Inténtalo de nuevo.');
      },
    );
  }

  // checkAvail de parques para la habitación real de una oferta. withTickets pide la lista de tickets de esa tarifa;
  // bookingCode permite consultar otra tarifa de la misma habitación (el ticketKey del ticket elegido).
  private availPayload(offer: any, real: any, withTickets: boolean, bookingCode?: string): any {
    const searchPayload = (JSON.parse(localStorage.getItem('payload') || '[]') || [])[0] || {};
    const children = (this.childAges || []).map((c: any) => c?.age ?? c);

    return {
      products: [
        {
          categoriesId: [4],
          providerId: this.index.provider(offer).id,
          productId: String(this.hotel.id ?? this.hotel.productId),
          arrivalDate: real.stay?.startDate || this.arrivalDate,
          departureDate: real.stay?.endDate || this.departureDate,
          agencyId: searchPayload.agencyId,
          raw_response: true, // respuesta cruda de checkAvail con rentabilidad en tickets (priceDeltaProf)
          nationality: searchPayload.nationality || 'US',
          others: {
            hotelType: searchPayload.others?.hotelType || this.hotelType,
            withTickets,
            hotels: {
              roomConfiguration: [
                {
                  adults: this.adults,
                  children,
                  roomId: real.room?.roomId,
                  roomCode: real.room?.roomCode,
                  bookingCode: bookingCode || real.room?.bookParam,
                },
              ],
            },
          },
        },
      ],
    };
  }

  // Ticket que reemplaza al incluido en la tarifa (Disney, tickets.mode === 'included_swap'): la tarifa que se reserva
  // es la del ticketKey del ticket elegido y no la que se usó para listar los tickets.
  private get swapTicket(): boolean {
    return this.withTickets && !this.addonTickets && this.tickets?.mode === 'included_swap' && !!this.ticket?.ticketKey;
  }

  // Clave con la que se confirma el resumen: el ticketKey del ticket elegido o, en Full of Dreams sin ticket (o con un
  // ticket add-on), la clave del plan. Vacía si no hay nada que confirmar.
  private get confirmKey(): string {
    if (this.swapTicket) {
      return String(this.ticket.ticketKey);
    }
    return this.checkAvail ? String(this.finalOffer?.rateKey || '') : '';
  }

  // Al llegar al resumen se confirma con checkAvail la tarifa del ticket elegido (su ticketKey), sin la lista de tickets.
  // Si ya no está o cambió de precio, no se deja agregar al carrito y se vuelven a pedir los tickets.
  private confirmTicket(): void {
    const ticket = this.ticket;
    const offer = this.finalOffer;
    const real = this.index.real(offer);
    const ticketKey = this.confirmKey;
    if (!ticketKey || !real || this.ticketConfirmedFor === ticketKey) {
      return;
    }

    this.loading = true;
    this.error = '';
    this.commit();
    this.restService.doPost('app', 'search/products/multiple', this.availPayload(offer, real, false, ticketKey)).subscribe(
      (res: any) => {
        this.loading = false;
        const found = this.extractResult(res);
        const room = found?.additionalInfo?.rooms?.[0]?.room;
        if (!room) {
          this.fail(this.extractError(res) || 'La tarifa elegida ya no está disponible. Elige otra.');
          return;
        }
        const confirmed = Number(room.pricing?.priceWithTax ?? 0);
        const expected = this.swapTicket ? Number(ticket.totalPrice ?? 0) : 0;
        if (confirmed > 0 && expected > 0 && Math.abs(confirmed - expected) > Math.max(1, expected * 0.005)) {
          this.notificationService.info('El precio del ticket cambió. Revisa los tickets con el valor actualizado.');
          this.tickets = null;
          this.ticketsFor = '';
          this.enter('tickets');
          return;
        }
        // El check devuelve la clave final (corregida si hacía falta): la reserva se hace con ella, no con la armada aquí.
        this.confirmedRateKey = String(found.price?.rateKey || ticketKey);
        this.confirmedPolicy = this.policyOf(found.additionalInfo.rooms[0]);
        this.ticketConfirmedFor = ticketKey;
        this.commit();
      },
      () => {
        this.loading = false;
        this.fail('No se pudo confirmar el ticket. Inténtalo de nuevo.');
      },
    );
  }

  retryConfirm(): void {
    this.ticketConfirmedFor = '';
    this.confirmTicket();
  }

  // Habitación de la respuesta de checkAvail (results[0].data.results[0].additionalInfo.rooms[0].room).
  private extractRoom(res: any): any {
    return this.extractResult(res)?.additionalInfo?.rooms?.[0]?.room || null;
  }

  // Resultado de checkAvail con habitación disponible.
  private extractResult(res: any): any {
    const candidates = [res?.results?.[0]?.data?.results?.[0], res?.results?.[0], res?.data?.results?.[0]];
    const found = candidates.find((c: any) => c?.additionalInfo?.rooms?.length);
    return found && found.price?.available !== false ? found : null;
  }

  // Tras recargar los tickets (otra tarifa) se conserva el tipo y los días si siguen disponibles.
  private remapTicketSelection(): void {
    if (!this.withTickets) {
      return;
    }
    const options: any[] = this.tickets?.options || [];
    if (!options.some(o => o.type === this.ticketMode)) {
      this.ticketMode = null;
      this.days = null;
      this.ticketKey = null;
      return;
    }
    this.pickTicket(this.ticketMode as string);
  }

  private fail(message: string): void {
    this.error = message;
    this.notificationService.info(message);
    this.commit();
  }

  // La respuesta puede venir como results[].data.results[] o results[].
  private extractTickets(res: any): any {
    const candidates = [res?.results?.[0]?.data?.results?.[0], res?.results?.[0], res?.data?.results?.[0]];
    return candidates.find((c: any) => c?.additionalInfo?.tickets)?.additionalInfo?.tickets || null;
  }

  // Octopus devuelve success:true con el fallo dentro de results[0] (error, message, errors).
  private extractError(res: any): string {
    const r = res?.results?.[0];
    if (!r?.error) {
      return '';
    }
    const details = ([] as string[]).concat(...(Object.values(r.errors || {}) as string[][])).join(' ');
    return [r.message, details].filter(Boolean).join(': ');
  }

  // ------------------------------------------------------------------ salida
  result(): IParkFlowResult {
    const offer = this.finalOffer;
    return {
      real: this.index.real(offer),
      offer,
      roomType: this.roomType,
      packageCode: this.packageCode,
      plan: this.plan,
      ticket: this.ticket,
      days: this.withTickets ? this.days : null,
      ticketDate: this.withTickets ? this.ticketDate : null,
      ticketKey: this.checkAvail ? this.confirmedRateKey || null : this.addonTickets || !this.ticket ? null : this.ticket.ticketKey || null,
      addon: this.addonTickets && !!this.ticket,
      accommodationTotal: this.accommodationTotal,
      netTotal: this.netTotal,
      currency: this.currency,
      accessKind: this.accessVariant?.kind || null,
    };
  }

  private lines(): string[] {
    if (!this.choice) {
      return [];
    }
    const lines = [`1X ${this.roomType.name}`];
    if (this.accessLabel) {
      lines.push(this.accessLabel);
    }
    if (this.withTickets && this.ticket) {
      lines.push(`${this.ticketTitle} · ${this.days} ${this.days === '1' ? 'día' : 'días'}`);
    } else if (this.ticketMode === 'none') {
      lines.push('Solo hotel');
    }
    if (this.visited.has('meals')) {
      lines.push(this.planName);
    }
    return lines;
  }

  // Filas del detalle de tarifas del pie: lo elegido hasta el paso actual.
  private rows(): IParkFareRow[] {
    if (!this.choice) {
      return [];
    }
    const b = this.breakdown;
    const room = `1X ${this.roomType.name}` + (this.accessLabel ? ` (${this.accessLabel})` : '');
    const rows: IParkFareRow[] = [{ label: 'Tarifa de Alojamiento', detail: room, amount: b.lodging }];
    if (this.withTickets && this.ticket) {
      rows.push({
        label: 'Tarifa de Ticket',
        detail: `${this.ticketTitle} ${this.days} ${this.days === '1' ? 'día' : 'días'} (${this.ticketPaxText})`,
        amount: b.ticket,
      });
    }
    if (this.visited.has('meals') && this.hasVariantPackages) {
      rows.push({ label: 'Tarifa Plan de Comidas', detail: this.planName, amount: b.meals });
    }
    return rows;
  }

  snapshot(): IParkFlowState {
    return {
      steps: this.steps,
      current: this.stepIndex + 1,
      maxReached: this.maxReached + 1,
      hasChoice: !!this.choice,
      canNext: this.canNext,
      canBack: this.stepIndex > 0,
      nextLabel: this.stepKey === 'summary' ? 'Agregar al carrito' : 'Continuar',
      loading: this.loading,
      lines: this.lines(),
      rows: this.rows(),
      summary: this.stepKey === 'summary',
      accommodationTotal: this.accommodationTotal,
      netTotal: this.netTotal,
      currency: this.currency,
    };
  }

  // Se emite en un microtask para no cambiar el estado del padre en plena detección de cambios.
  private commit(): void {
    Promise.resolve().then(() => {
      this.stateChange.emit(this.snapshot());
      this.cdr.markForCheck();
    });
  }
}
